import { SerializedStyles, css, useTheme } from "@emotion/react"
import React from "react"
import striptags from "striptags"

import Button from "./Button"
import GridCol from "./GridCol"
import GridContainer from "./GridContainer"
import Heading from "./Heading"

import { UpsunTheme } from "../themes"
import { pxToRem, respSpacing } from "../styles"
import { subtitleTags } from "../helpers/striptagsDefaults"

type CallToActionProps = {
  text?: string,
  subtitle?: string,
  highlightedText?: string,
  btnHref: string,
  btnText: string,
  btn2Href?: string,
  btn2Text?: string,
  css?: SerializedStyles | SerializedStyles[]
}

const CallToAction: React.FC<CallToActionProps> = ({
  text,
  subtitle,
  highlightedText,
  btnText,
  btnHref,
  btn2Text,
  btn2Href,
  ...props
}) => {
  const theme = useTheme() as UpsunTheme

  const gradient = css`
    display: inline-block;
    background: rgb(250, 65, 255);
    background: linear-gradient(90deg, #806bff 0%, #ed49f0 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  `

  const colStyles = css`
    ${theme.name === "light"
      ? `
        background:
          linear-gradient(
            180deg,
            rgba(247,248,248,1) 0%,
            rgba(255,255,255,0.01) 66%,
            rgba(255,255,255,0.02) 100%
          ),
          linear-gradient(
            53deg,
            rgba(223, 218, 255, 1) 0%,
            rgba(253, 232, 253, 1) 34%,
            rgba(255, 237, 231, 1) 67%,
            rgba(247, 254, 204, 1) 100%
          );
    `
      : `
        background:
          linear-gradient(
            0deg,
              #191C1EFF 0%,
              #191C1E01 100%
            );
      `};

    text-align: center;
    ${respSpacing('padding-block', '4xl')}
  `;

  const headingStyles = css`
    h2:first-of-type {
      margin-bottom: ${subtitle ? pxToRem(16) : 0};
    }
  `;

  let subtitleSafeHTMLString = "";

  if(subtitle) {
    subtitleSafeHTMLString = striptags(subtitle, subtitleTags,"");
  }

  const btnContainerStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;

    ${respSpacing('margin-block-start', 'm')}
    ${respSpacing('gap', '2xs')}

    a {
      min-width: ${pxToRem(245)};
    }
  `;

  const noMargin = css`
    margin-bottom: 0;
  `;

  // @todo use TextWithHighlight component for this
  const valuePropMatch = text?.match(/_(.*)_/);
  let splitTitle = [];
  let ctaText;

  if (valuePropMatch) {
    const valueProp = valuePropMatch[0];
    splitTitle = text?.split(valueProp) || [];

    if(splitTitle.length >= 2) {
      ctaText = <>
        <span dangerouslySetInnerHTML={{__html: splitTitle[0]}} />
        <span css={gradient}>{valueProp.replaceAll("_", "")}</span>
        <span dangerouslySetInnerHTML={{__html: splitTitle[1]}} />
      </>
    }
  } else {
    if(highlightedText) {
      ctaText = <>
        {text}<br/>
        <span css={gradient}>{highlightedText}</span>
      </>
    } else {
      ctaText = text;
    }
  }

  return (
    <div css={colStyles} {...props}>
      <GridContainer>
        <GridCol colStart={1} colEnd={13} colStartMd={3} colEndMd={11}>
          <div css={[headingStyles]}>
            <Heading level={2}>{ctaText}</Heading>
            {subtitle && 
              <p 
                dangerouslySetInnerHTML={{__html: subtitleSafeHTMLString}} 
                css={noMargin}
              />
            }
          </div>
          <div css={[btnContainerStyles]}>
            <Button level="primary" href={btnHref} text={btnText} />
            {btn2Text && btn2Href && <Button level="secondary" href={btn2Href} text={btn2Text}/>}
          </div>
        </GridCol>
      </GridContainer>
    </div>
  )
}

export default CallToAction
